import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/zh-cn";
import { useDataProvider, useNotify } from "react-admin";
import type {
  AgentDailyReport,
  AgentMatchOrderStats,
  AgentRelatedOrder,
} from "../../models/AgentDispatchRecord";
import type { AppDataProvider } from "../../types/dataProvider";
import { formatDate } from "../../utils/common";
import { AgentRelatedOrderDetailDialog } from "./agent_related_order_detail";

type LoadState = "loading" | "ready" | "error";

/**
 * 将日期格式化为接口所需的 day_key。
 */
const formatDayKey = (value: Dayjs | null): string => {
  if (!value || !value.isValid()) {
    return "";
  }
  return value.format("YYYY-MM-DD");
};

/**
 * 格式化时间展示。
 */
const formatDateValue = (value: string): string => {
  if (!value) {
    return "-";
  }
  return formatDate(value);
};

/**
 * 布尔标记 Chip。
 */
const BoolChip: React.FC<{ value: boolean; trueLabel?: string; falseLabel?: string }> = ({
  value,
  trueLabel = "是",
  falseLabel = "否",
}) => (
  <Chip
    label={value ? trueLabel : falseLabel}
    size="small"
    color={value ? "success" : "default"}
    variant={value ? "filled" : "outlined"}
  />
);

/**
 * 紧凑基础指标。
 */
const MetricItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.75,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      minWidth: 96,
      flex: "1 1 96px",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
      {label}
    </Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

/**
 * 紧凑匹配汇总项（司机 / 配对 / 订单）。
 */
const MatchStatsItem: React.FC<{ title: string; stats: AgentMatchOrderStats }> = ({
  title,
  stats,
}) => (
  <Box
    sx={{
      px: 1.25,
      py: 0.75,
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 1,
      minWidth: 160,
      flex: "1 1 160px",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
      {title}
    </Typography>
    <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        司机 {stats.drivers}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        配对 {stats.pairs}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        订单 {stats.orders}
      </Typography>
    </Stack>
  </Box>
);

/**
 * 智能体日志记录（按日汇总）页面。
 */
const AgentLogPage: React.FC = () => {
  const dataProvider = useDataProvider() as AppDataProvider;
  const notify = useNotify();

  const [selectedDate, setSelectedDate] = React.useState<Dayjs | null>(dayjs());
  const [report, setReport] = React.useState<AgentDailyReport | null>(null);
  const [loadState, setLoadState] = React.useState<LoadState>("loading");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [refreshing, setRefreshing] = React.useState(false);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selectedOrder, setSelectedOrder] = React.useState<AgentRelatedOrder | null>(null);

  const dayKey = formatDayKey(selectedDate);

  const fetchReport = React.useCallback(
    async (mode: "initial" | "refresh", nextDayKey: string) => {
      if (!nextDayKey) {
        setErrorMessage("请选择有效日期");
        setLoadState("error");
        setReport(null);
        return;
      }

      if (mode === "initial") {
        setLoadState("loading");
      } else {
        setRefreshing(true);
      }
      setErrorMessage("");

      try {
        const response = await dataProvider.getAgentDailyReport(nextDayKey);
        setReport(response.data);
        setLoadState("ready");
      } catch (error) {
        const message = error instanceof Error ? error.message : "日志报告加载失败";
        setErrorMessage(message);
        setReport(null);
        setLoadState("error");
        notify(`日志报告加载失败：${message}`, { type: "error" });
      } finally {
        setRefreshing(false);
      }
    },
    [dataProvider, notify]
  );

  React.useEffect(() => {
    void fetchReport("initial", dayKey);
  }, [fetchReport, dayKey]);

  const handleRefresh = () => {
    void fetchReport("refresh", dayKey);
  };

  /**
   * 打开关联订单详情。
   */
  const handleOpenDetail = (order: AgentRelatedOrder) => {
    setSelectedOrder(order);
    setDetailOpen(true);
  };

  /**
   * 关闭关联订单详情。
   */
  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedOrder(null);
  };

  const renderBody = () => {
    if (loadState === "loading") {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={240}
          gap={2}
        >
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            正在加载 {dayKey} 的日志报告...
          </Typography>
        </Box>
      );
    }

    if (loadState === "error") {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              重试
            </Button>
          }
        >
          {errorMessage || "日志报告加载失败"}
        </Alert>
      );
    }

    if (!report) {
      return (
        <Typography variant="body2" color="text.secondary">
          暂无报告数据
        </Typography>
      );
    }

    const { statistics, relatedOrders } = report;
    const hasContent =
      statistics.dispatchTotalRecords > 0 ||
      statistics.dispatchUniquePairs > 0 ||
      statistics.dispatchUniqueDrivers > 0 ||
      relatedOrders.length > 0;

    if (!hasContent) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={180}
          gap={1}
        >
          <Typography variant="body1">当日暂无日志数据</Typography>
          <Typography variant="body2" color="text.secondary">
            可切换其他日期查看
          </Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <MetricItem label="调度总记录" value={String(statistics.dispatchTotalRecords)} />
          <MetricItem label="唯一配对" value={String(statistics.dispatchUniquePairs)} />
          <MetricItem label="唯一司机" value={String(statistics.dispatchUniqueDrivers)} />
          <MatchStatsItem title="匹配订单" stats={statistics.matchedAnyOrder} />
          <MatchStatsItem title="匹配且未取消" stats={statistics.matchedNonCanceledOrder} />
          <MatchStatsItem title="首次推送后创建" stats={statistics.matchedCreatedAfterFirstPush} />
          <MatchStatsItem
            title="首次推送后创建且未取消"
            stats={statistics.matchedCreatedAfterFirstPushNonCanceled}
          />
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle2">关联订单（{relatedOrders.length}）</Typography>
          <Typography variant="caption" color="text.secondary">
            点击行查看详情
          </Typography>
        </Stack>

        {relatedOrders.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            当日暂无关联订单
          </Typography>
        ) : (
          <TableContainer
            sx={{
              flex: 1,
              minHeight: 240,
              maxHeight: "calc(100vh - 280px)",
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>货源</TableCell>
                  <TableCell>司机</TableCell>
                  <TableCell>车牌</TableCell>
                  <TableCell>货主</TableCell>
                  <TableCell>订单 ID</TableCell>
                  <TableCell>推送次数</TableCell>
                  <TableCell>首次推送</TableCell>
                  <TableCell>订单创建</TableCell>
                  <TableCell>已取消</TableCell>
                  <TableCell>推送后创建</TableCell>
                  <TableCell>推送后创建且未取消</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {relatedOrders.map((order, index) => (
                  <TableRow
                    key={order.orderId || `${order.driverId}-${order.cargoName}-${index}`}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleOpenDetail(order)}
                  >
                    <TableCell>{order.cargoName || order.cargoId || "-"}</TableCell>
                    <TableCell>{order.driverName || order.driverId || "-"}</TableCell>
                    <TableCell>{order.truckPlate || "-"}</TableCell>
                    <TableCell>{order.shipperName || order.shipperId || "-"}</TableCell>
                    <TableCell sx={{ maxWidth: 160, wordBreak: "break-all" }}>
                      {order.orderId || "-"}
                    </TableCell>
                    <TableCell>{order.pushCount}</TableCell>
                    <TableCell>{formatDateValue(order.firstPushedAt)}</TableCell>
                    <TableCell>{formatDateValue(order.orderCreatedAt)}</TableCell>
                    <TableCell>
                      <BoolChip value={order.orderCanceled} trueLabel="已取消" falseLabel="否" />
                    </TableCell>
                    <TableCell>
                      <BoolChip value={order.orderCreatedAfterFirstPush} />
                    </TableCell>
                    <TableCell>
                      <BoolChip value={order.orderCreatedAfterFirstPushNonCanceled} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 1.5, height: "100%", boxSizing: "border-box" }}>
      <Paper
        sx={{
          p: 1.5,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 96px)",
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          mb={1.25}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              智能体日志记录
            </Typography>
            <Typography variant="caption" color="text.secondary">
              按日查看智能体调度汇总报告
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {report?.scope?.start && report?.scope?.end && (
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                统计范围：{formatDateValue(report.scope.start)} ~ {formatDateValue(report.scope.end)}
              </Typography>
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="zh-cn">
              <DatePicker
                label="日期"
                value={selectedDate}
                onChange={(value) => setSelectedDate(value)}
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
              />
            </LocalizationProvider>
            <Tooltip title="刷新">
              <span>
                <IconButton onClick={handleRefresh} disabled={refreshing || loadState === "loading"}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {renderBody()}
        </Box>
      </Paper>

      <AgentRelatedOrderDetailDialog
        open={detailOpen}
        order={selectedOrder}
        onClose={handleCloseDetail}
      />
    </Box>
  );
};

export default AgentLogPage;
